import React, { FC, useCallback, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonMenuButton,
  IonPage,
  IonRow,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { checkmarkCircle, closeCircle, timeOutline } from "ionicons/icons";
import { IPaymentReceipt } from "../../models";
import { NumberMoney } from "../../components/NumberMoney/NumberMoney";
import InfoMessage from "../../components/InfoMessage/InfoMessage";
import { PaymentBrands } from "../../components/PaymentBrands/PaymentBrands";
import { framesText } from "../../common/utils";
import { useCart } from "../Cart/useCart";
import {
  getPaymentStatusApi,
  retryPaymentApi,
  switchToCodApi,
} from "./paymentApi";
import { clearPendingToken, isPendingToken, submitGatewayForm } from "./gateway";

/* Texts required by Banca Intesa (EPM rules 2.7). Never show the decline reason. */
const TEXT_SUCCESS =
  "Uspešno ste izvršili plaćanje – račun Vaše platne kartice je zadužen.";
const TEXT_FAILURE =
  "Plaćanje nije uspešno, račun Vaše platne kartice nije zadužen. Najčešći uzrok je pogrešno unet broj kartice, datum isteka ili sigurnosni kod. Pokušajte ponovo, a u slučaju uzastopnih grešaka pozovite Vašu banku.";

const Row: FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <IonRow>
    <IonCol size="6" style={{ color: "var(--ion-color-dark-shade)" }}>
      {label}
    </IonCol>
    <IonCol size="6" className="b" style={{ wordBreak: "break-word" }}>
      {value || "-"}
    </IonCol>
  </IonRow>
);

const Money: FC<{ value: number }> = ({ value }) => (
  <NumberMoney value={value} forceCurrency="DIN" />
);

type Props = RouteComponentProps<{ token: string }>;

const PaymentResult: FC<Props> = ({ match }) => {
  const token = match.params.token;
  const { resetCart } = useCart();
  const [receipt, setReceipt] = useState<IPaymentReceipt | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await getPaymentStatusApi(token);
    setReceipt(data);
    setLoading(false);
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // The order just paid (or switched to cash on delivery) is done: clear the local cart.
  // Only for the order this browser sent to the bank, so an old receipt link never wipes a new cart.
  useEffect(() => {
    if (receipt && (receipt.success || receipt.paymentMethod === "COD") && isPendingToken(token)) {
      resetCart();
      clearPendingToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipt?.success, receipt?.paymentMethod]);

  const retry = async () => {
    setBusy(true);
    setError(null);
    const result = await retryPaymentApi(token);
    if (result.ok) {
      submitGatewayForm(result.form);
    } else {
      setError(result.error);
      setBusy(false);
    }
  };

  const switchToCod = async () => {
    setBusy(true);
    setError(null);
    const result = await switchToCodApi(token);
    if (result.ok) {
      setReceipt(result.receipt);
    } else {
      setError(result.error);
    }
    setBusy(false);
  };

  const isPending = receipt?.paymentMethod === "CARD" && receipt?.status === "PENDING";
  const isCod = receipt?.paymentMethod === "COD";

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="success">
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Potvrda o plaćanju</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonGrid>
          <IonRow>
            <IonCol size="12" sizeMd="8" offsetMd="2" sizeLg="6" offsetLg="3">
              {loading && (
                <div className="ion-text-center" style={{ padding: 40 }}>
                  <IonSpinner name="crescent" />
                </div>
              )}

              {!loading && !receipt && (
                <InfoMessage
                  level="warning"
                  text="Porudžbina nije pronađena. Ako ste platili karticom, proverite email ili nas kontaktirajte."
                />
              )}

              {!loading && receipt && (
                <>
                  {/* 1. Outcome and its effect on the card account */}
                  <IonCard>
                    <IonCardContent className="ion-text-center">
                      {isCod ? (
                        <>
                          <IonIcon icon={checkmarkCircle} color="success" style={{ fontSize: 64 }} />
                          <h2>Porudžbina je prihvaćena</h2>
                          <p>Plaćanje pouzećem, prilikom preuzimanja paketa. Račun Vaše platne kartice nije zadužen.</p>
                        </>
                      ) : isPending ? (
                        <>
                          <IonIcon icon={timeOutline} color="warning" style={{ fontSize: 64 }} />
                          <h2>Plaćanje je u obradi</h2>
                          <p>
                            Još nismo dobili odgovor banke. Osvežite za nekoliko minuta. Potvrdu
                            ćete dobiti i na email.
                          </p>
                          <IonButton onClick={load} disabled={busy}>
                            Osveži
                          </IonButton>
                        </>
                      ) : receipt.success ? (
                        <>
                          <IonIcon icon={checkmarkCircle} color="success" style={{ fontSize: 64 }} />
                          <h2>{TEXT_SUCCESS}</h2>
                          <p>Hvala na poverenju. Potvrdu smo poslali na {receipt.email}.</p>
                        </>
                      ) : (
                        <>
                          <IonIcon icon={closeCircle} color="danger" style={{ fontSize: 64 }} />
                          <h2>{TEXT_FAILURE}</h2>
                        </>
                      )}

                      {receipt.canRetry && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 16 }}>
                          <IonButton onClick={retry} disabled={busy}>
                            {busy ? <IonSpinner name="crescent" /> : "Pokušaj ponovo karticom"}
                          </IonButton>
                          <IonButton color="medium" fill="outline" onClick={switchToCod} disabled={busy}>
                            Plati pouzećem
                          </IonButton>
                        </div>
                      )}
                      {error && (
                        <div style={{ marginTop: 12 }}>
                          <InfoMessage level="danger" text={error} />
                        </div>
                      )}
                    </IonCardContent>
                  </IonCard>

                  {/* 3. Order */}
                  <IonCard>
                    <IonCardContent>
                      <IonText>
                        <h2>Porudžbina broj {receipt.orderNumber}</h2>
                      </IonText>
                      <IonGrid>
                        <IonRow className="b">
                          <IonCol size="5">Proizvod</IonCol>
                          <IonCol size="3" className="ion-text-right">Cena</IonCol>
                          <IonCol size="1" className="ion-text-center">Kol.</IonCol>
                          <IonCol size="3" className="ion-text-right">Ukupno</IonCol>
                        </IonRow>
                        {receipt.items.map((item, i) => (
                          <IonRow key={i}>
                            <IonCol size="5">
                              {item.title}
                              <br />
                              <IonText color="medium" style={{ fontSize: 12 }}>
                                {item.code}
                              </IonText>
                              {framesText(item.frames, item.framesAssembled) && (
                                <>
                                  <br />
                                  <IonText style={{ fontSize: 12 }}>
                                    Ram: {framesText(item.frames, item.framesAssembled)}
                                  </IonText>
                                </>
                              )}
                            </IonCol>
                            <IonCol size="3" className="ion-text-right"><Money value={item.unitPrice} /></IonCol>
                            <IonCol size="1" className="ion-text-center">{item.quantity}</IonCol>
                            <IonCol size="3" className="ion-text-right"><Money value={item.total} /></IonCol>
                          </IonRow>
                        ))}
                        <IonRow>
                          <IonCol size="9">Dostava</IonCol>
                          <IonCol size="3" className="ion-text-right"><Money value={receipt.shipping} /></IonCol>
                        </IonRow>
                        <IonRow className="b">
                          <IonCol size="9">Ukupno</IonCol>
                          <IonCol size="3" className="ion-text-right"><Money value={receipt.total} /></IonCol>
                        </IonRow>
                      </IonGrid>
                      {receipt.reserved?.length > 0 && (
                        <div style={{ margin: "8px 0" }}>
                          <b>Rezervisano</b>
                          <IonText color="medium">
                            {" "}
                            (nije na stanju, ne naplaćuje se sada; javićemo Vam se kada stigne)
                          </IonText>
                          {receipt.reserved.map((r, i) => (
                            <div key={i}>
                              {r.title}{" "}
                              <IonText color="medium" style={{ fontSize: 12 }}>
                                {r.code}
                              </IonText>{" "}
                              × {r.quantity}
                            </div>
                          ))}
                        </div>
                      )}
                      {receipt.merchant.vatStatement && (
                        <IonText color="medium" style={{ fontSize: 13 }}>
                          {receipt.merchant.vatStatement}
                        </IonText>
                      )}
                    </IonCardContent>
                  </IonCard>

                  {/* 2. Customer */}
                  <IonCard>
                    <IonCardContent>
                      <IonText><h2>Kupac</h2></IonText>
                      <IonGrid>
                        <Row label="Ime i prezime" value={receipt.name} />
                        <Row label="Email" value={receipt.email} />
                        <Row label="Telefon" value={receipt.phone} />
                        <Row
                          label="Adresa i adresa za dostavu"
                          value={`${receipt.address}, ${receipt.zip} ${receipt.place}`}
                        />
                      </IonGrid>
                    </IonCardContent>
                  </IonCard>

                  {/* 5. Transaction data assigned by the bank */}
                  {receipt.bank && (
                    <IonCard>
                      <IonCardContent>
                        <IonText><h2>Podaci o transakciji</h2></IonText>
                        <IonGrid>
                          <Row label="Broj narudžbine (Order ID)" value={receipt.bank.oid} />
                          <Row label="Autorizacioni kod" value={receipt.bank.authCode} />
                          <Row label="Status transakcije" value={receipt.bank.response} />
                          <Row label="Kod statusa transakcije" value={receipt.bank.procReturnCode} />
                          <Row label="Statusni kod 3D transakcije" value={receipt.bank.mdStatus} />
                          <Row label="Datum transakcije" value={receipt.bank.trxDate} />
                          <Row label="ID transakcije" value={receipt.bank.transId} />
                        </IonGrid>
                      </IonCardContent>
                    </IonCard>
                  )}

                  {/* 4. Merchant */}
                  <IonCard>
                    <IonCardContent>
                      <IonText><h2>Prodavac</h2></IonText>
                      <IonGrid>
                        <Row label="Naziv" value={receipt.merchant.name} />
                        <Row label="Adresa" value={receipt.merchant.address} />
                        <Row label="PIB" value={receipt.merchant.pib} />
                        {receipt.merchant.mb && <Row label="MB" value={receipt.merchant.mb} />}
                      </IonGrid>
                    </IonCardContent>
                  </IonCard>

                  <PaymentBrands />
                </>
              )}
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonContent>
    </IonPage>
  );
};

export default PaymentResult;
